import { isContentAdminRoute } from '~/composables/useContentLockedRoute';
import { CONTENT_ROUTE_LOGIN } from '~/constants/routes/content';
import { AdminMfaStatus } from '~/graphql/generated/graphql';

import { isSafeRedirectPath } from './redirect';

export const AdminMfaRouteDecision = {
  none: 'none',
  stay: 'stay',
  redirect: 'redirect',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type AdminMfaRouteDecision = (typeof AdminMfaRouteDecision)[keyof typeof AdminMfaRouteDecision];

interface AdminMfaRouteDecisionInput {
  path: string;
  status: AdminMfaStatus | null | undefined;
}

interface AdminMfaRequiredHandlerDeps {
  fetchUser: () => Promise<unknown>;
  navigate: (fullPath: string) => unknown;
  currentFullPath: () => string;
}

const withoutQueryOrHash = (path: string): string => path.split(/[?#]/)[0] ?? path;

/** A signed-in admin whose session has not passed the second factor yet. */
export const isAdminMfaPending = (status: AdminMfaStatus | null | undefined): boolean =>
  status === AdminMfaStatus.EnrolmentRequired || status === AdminMfaStatus.ChallengeRequired;

/** Whether a path, with or without its cluster prefix, query or hash, is the admin login. */
export const isAdminLoginPath = (path: string): boolean =>
  withoutQueryOrHash(clusterRelativePath(path)) === CONTENT_ROUTE_LOGIN;

/**
 * Where an admin's second factor leaves a navigation: `none` when nothing is owed, `stay` on the admin
 * login that opens on the enrolment or challenge step, `redirect` from anywhere else.
 */
export const adminMfaRouteDecision = ({ path, status }: AdminMfaRouteDecisionInput): AdminMfaRouteDecision => {
  if (!isAdminMfaPending(status)) {
    return AdminMfaRouteDecision.none;
  }

  return isAdminLoginPath(path) ? AdminMfaRouteDecision.stay : AdminMfaRouteDecision.redirect;
};

/**
 * Sends an admin who still owes the second factor to the admin login, which opens on the enrolment or
 * challenge step. Only an admin-app path is carried back as `redirect`; from any other app the login
 * lands on the admin dashboard, reached with a full page load because it is a different app. That load
 * targets a path, not a URL: the admin app shares the host, and a server-built URL can carry the
 * internal `http` scheme, on which the second-factor mutations are refused.
 */
export const navigateToAdminMfaLogin = (fullPath: string) => {
  const current = clusterRelativePath(fullPath);

  if (!isContentAdminRoute(withoutQueryOrHash(current))) {
    return navigateTo(`${useCurrentCluster().pathPrefix.value}${CONTENT_ROUTE_LOGIN}`, { replace: true, external: true });
  }

  return navigateTo(
    { path: clusterRedirectPath(CONTENT_ROUTE_LOGIN), query: isSafeRedirectPath(current) ? { redirect: current } : {} },
    { replace: true },
  );
};

/**
 * Answers an MFA_REQUIRED refusal. The backend refuses every operation but the sign-in ones until the
 * session passes the second factor, so the viewer is re-read first for the route guards to see the
 * pending status, then sent to the admin login from whichever page refused. Refusals arriving while a
 * run is in flight, or on the admin login itself, are left alone.
 */
export const createAdminMfaRequiredHandler = ({ fetchUser, navigate, currentFullPath }: AdminMfaRequiredHandlerDeps) => {
  let inFlight = false;

  return async (): Promise<void> => {
    const refusedFullPath = currentFullPath();

    if (inFlight || isAdminLoginPath(refusedFullPath)) {
      return;
    }

    inFlight = true;

    try {
      await fetchUser().catch((error: unknown) => {
        console.error('[adminMfa] re-reading the viewer after an MFA_REQUIRED refusal failed', error);
      });
      await navigate(refusedFullPath);
    }
    finally {
      inFlight = false;
    }
  };
};
